import { useEffect, useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CalendarDays,
  Check,
  Copy,
  Globe,
  Lock,
  Mail,
  MapPin,
  Phone,
  ShieldCheck,
  EyeOff,
} from 'lucide-react';
import {
  CATALOG_TYPE_LABELS,
  LEVEL_LABELS,
  ORG_DIRECTORY_PAGE_SIZE,
  ORG_SOCIAL_LABELS,
  ORG_SOCIAL_NETWORKS,
  ORG_TYPES,
  ORG_TYPE_LABELS,
  formatPhone,
  type OrgType,
} from '@ready/shared';
import { useApi, type OrgOffering, type OrgProfile } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useMe } from '@/lib/me';
import { formatSessionTime } from '@/lib/dates';
import { LINKEDIN_TAG_TIP, linkedInPostText } from '@/lib/linkedin';
import { LinkedInShareButton, NativeShareButton } from '@/components/share-buttons';
import { isManager } from '@/lib/org-roles';
import { CatalogCard, SearchBox, formatMinutes } from '@/components/catalog';
import { MarkdownContent } from '@/components/course/markdown';
import { OrgLogo, VerifiedBadge } from '@/components/org-brand';
import { Filter, Pager } from '@/components/people';
import { Badge, Button, Card, ErrorText } from '@/components/ui';

const COUNTRIES = new Intl.DisplayNames(['en-US'], { type: 'region' });
const countryName = (code: string) => COUNTRIES.of(code) ?? code;
const typeLabel = (t: OrgType) => (t === 'nirvik' ? 'Organization' : ORG_TYPE_LABELS[t]);
const place = (o: { city: string | null; country: string }) =>
  o.city ? `${o.city}, ${countryName(o.country)}` : countryName(o.country);

/** `/o/:slug`: an organization's profile. */
export function OrgPublicProfilePage() {
  const { slug = '' } = useParams();
  const api = useApi();
  const result = useQuery({
    queryKey: ['orgProfilePage', slug],
    queryFn: () => api.orgProfile(slug),
    retry: false,
  });
  if (result.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (result.isError)
    return (
      <Card title="Organization not found">
        <p className="text-sm text-muted-foreground">
          This profile doesn&rsquo;t exist or isn&rsquo;t public.{' '}
          <Link to="/o" className="font-medium text-primary hover:underline">
            Browse organizations
          </Link>
        </p>
      </Card>
    );
  if ('redirect' in result.data) return <Navigate to={`/o/${result.data.redirect}`} replace />;
  return <Profile p={result.data.profile} />;
}

function Profile({ p }: { p: OrgProfile }) {
  const { user } = useAuth();
  const api = useApi();
  const myOrgs = useQuery({ queryKey: ['myOrgs'], queryFn: api.myOrgs, enabled: Boolean(user) });
  const canEdit = isManager(myOrgs.data?.orgs.find((o) => o.id === p.id)?.role ?? null);
  const links = ORG_SOCIAL_NETWORKS.filter((n) => p.socialLinks[n]);
  const hasContact = Boolean(p.websiteUrl || p.contactEmail || p.contactPhone || links.length);

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
      <div className="min-w-0 space-y-6">
        <Card>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
            <OrgLogo name={p.name} url={p.logoUrl?.replace('/128.png', '/400.png')} size="xl" />
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold">
                {p.name}
                {p.verified && (
                  <VerifiedBadge className="ml-1.5 inline-block size-6 align-[-0.2em]" />
                )}
              </h1>
              <p className="mt-1 flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
                <span>{typeLabel(p.type)}</span>
                <span aria-hidden>·</span>
                <span className="inline-flex items-center gap-1">
                  <MapPin className="size-3.5" aria-hidden />
                  {place(p)}
                </span>
              </p>
              {p.tagline && <p className="mt-3 text-muted-foreground">{p.tagline}</p>}
              <div className="mt-3 flex flex-wrap gap-2">
                {p.verified && (
                  <Badge>
                    <ShieldCheck className="mr-1 inline size-3.5 align-[-2px]" aria-hidden />
                    Verified by Nirvik Ready
                  </Badge>
                )}
                {p.isMember && <Badge>You&rsquo;re a member</Badge>}
                {!p.profilePublic && (
                  <Badge>
                    <Lock className="mr-1 inline size-3.5 align-[-2px]" aria-hidden />
                    Members only
                  </Badge>
                )}
              </div>
            </div>
          </div>
          {canEdit && (
            <Link
              to={`/organizations/${p.id}/profile`}
              className="mt-4 inline-block rounded-[var(--radius)] border border-border px-3 py-2 text-sm font-medium hover:bg-muted"
            >
              Edit profile
            </Link>
          )}
        </Card>

        {p.about && (
          <Card title="About">
            <MarkdownContent markdown={p.about} />
          </Card>
        )}

        <Offerings p={p} />
      </div>

      <aside className="min-w-0 space-y-6">
        {(hasContact || p.contactForMembers) && (
          <Card title="Contact">
            <ul className="space-y-3 text-sm">
              {p.websiteUrl && (
                <ContactLink icon={Globe} href={p.websiteUrl}>
                  {p.websiteUrl.replace(/^https:\/\/(www\.)?/, '').replace(/\/$/, '')}
                </ContactLink>
              )}
              {p.contactEmail && (
                <ContactLink icon={Mail} href={`mailto:${p.contactEmail}`}>
                  {p.contactEmail}
                </ContactLink>
              )}
              {p.contactPhone && (
                <ContactLink icon={Phone} href={`tel:${p.contactPhone}`}>
                  {formatPhone(p.contactPhone)}
                </ContactLink>
              )}
              {p.contactForMembers && (
                <li className="flex items-start gap-2 text-muted-foreground">
                  <Lock className="mt-0.5 size-4 shrink-0" aria-hidden />
                  Email and phone are shown to members.
                </li>
              )}
            </ul>
            {links.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {links.map((n) => (
                  <a
                    key={n}
                    href={p.socialLinks[n]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-full border border-border px-3 py-1.5 text-sm hover:bg-muted"
                  >
                    {ORG_SOCIAL_LABELS[n]}
                  </a>
                ))}
              </div>
            )}
          </Card>
        )}
        {(p.parent || p.children.length > 0) && (
          <Card title="Organization">
            {p.parent && (
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Part of
                </p>
                <OrgRow org={p.parent} />
              </div>
            )}
            {p.children.length > 0 && (
              <div className={p.parent ? 'mt-4' : undefined}>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Organizations under {p.name}
                </p>
                {p.children.map((c) => (
                  <OrgRow key={c.id} org={c} />
                ))}
              </div>
            )}
          </Card>
        )}
        {p.profilePublic && <ShareProfile p={p} />}
        {!p.isMember && (
          <Card title="Are you a member?">
            <p className="text-sm text-muted-foreground">
              Got an invite code from {p.name}? Join to see everything they offer their members.
            </p>
            <Link
              to="/join"
              className="mt-3 inline-block w-full rounded-[var(--radius)] bg-primary px-4 py-2 text-center text-sm font-medium text-primary-foreground hover:opacity-90 sm:w-auto"
            >
              Join with a code
            </Link>
          </Card>
        )}
        <AdminControls p={p} />
      </aside>
    </div>
  );
}

function ContactLink({
  icon: Icon,
  href,
  children,
}: {
  icon: typeof Globe;
  href: string;
  children: string;
}) {
  const external = href.startsWith('https://');
  return (
    <li>
      <a
        href={href}
        {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        className="-my-2 flex min-w-0 items-start gap-2 py-2 hover:underline"
      >
        <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
        <span className="break-all">{children}</span>
      </a>
    </li>
  );
}

function OrgRow({ org }: { org: OrgProfile['children'][number] }) {
  const inner = (
    <>
      <OrgLogo name={org.name} url={org.logoUrl} size="sm" />
      <span className="min-w-0 truncate">{org.name}</span>
      {org.verified && <VerifiedBadge />}
    </>
  );
  return org.profilePublic ? (
    <Link to={`/o/${org.slug}`} className="flex items-center gap-2 py-2 text-sm hover:underline">
      {inner}
    </Link>
  ) : (
    <p className="flex items-center gap-2 py-2 text-sm">{inner}</p>
  );
}

/**
 * Members see everything the org offers them (with their progress); everyone
 * else sees what it offers the public.
 */
function Offerings({ p }: { p: OrgProfile }) {
  const api = useApi();
  const { user } = useAuth();
  const member = Boolean(user && p.isMember);
  const catalog = useQuery({
    queryKey: ['catalog', { source: p.id, limit: 24 }],
    queryFn: () => api.catalog({ source: p.id, limit: 24, sort: 'newest' }),
    enabled: member,
  });
  if (member) {
    if (catalog.isPending) return null;
    if (catalog.isError) return <ErrorText error={catalog.error} />;
    return (
      <section>
        <h2 className="mb-3 text-lg font-semibold">What {p.name} offers you</h2>
        {catalog.data.items.length ? (
          <div className="grid gap-4 sm:grid-cols-2">
            {catalog.data.items.map((i) => (
              <CatalogCard key={`${i.kind}-${i.id}`} item={i} />
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Nothing published yet.</p>
        )}
        {catalog.data.total > catalog.data.items.length && (
          <Link
            to={`/learn?source=${p.id}`}
            className="-my-2 mt-3 inline-block py-2 text-sm font-medium text-primary hover:underline"
          >
            See all {catalog.data.total}
          </Link>
        )}
      </section>
    );
  }
  if (!p.offerings.length) return null;
  return (
    <section>
      <h2 className="mb-3 text-lg font-semibold">Open to everyone</h2>
      <div className="grid gap-4 sm:grid-cols-2">
        {p.offerings.map((o) => (
          <OfferingCard key={`${o.kind}-${o.slug}`} o={o} />
        ))}
      </div>
    </section>
  );
}

function OfferingCard({ o }: { o: OrgOffering }) {
  const path = o.kind === 'course' ? 'c' : o.kind === 'quiz' ? 'q' : 'e';
  const meta = [
    LEVEL_LABELS[o.level],
    formatMinutes(o.minutes),
    o.kind === 'course' && o.lessonCount
      ? `${o.lessonCount} lesson${o.lessonCount === 1 ? '' : 's'}`
      : o.kind === 'quiz' && o.questionCount
        ? `${o.questionCount} questions`
        : null,
  ].filter(Boolean);
  return (
    <Card className="flex h-full flex-col justify-between gap-4">
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{CATALOG_TYPE_LABELS[o.type]}</p>
        <p className="mt-1 font-medium">{o.title}</p>
        {o.kind === 'event' && o.startsAt && o.endsAt && (
          <p className="mt-1 flex items-start gap-1.5 text-sm font-medium text-primary">
            <CalendarDays className="mt-0.5 size-4 shrink-0" aria-hidden />
            {formatSessionTime(o.startsAt, o.endsAt)}
          </p>
        )}
        {o.summary && (
          <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{o.summary}</p>
        )}
        {o.kind !== 'event' && (
          <p className="mt-2 text-xs text-muted-foreground">{meta.join(' · ')}</p>
        )}
      </div>
      <Link
        to={`/${path}/${o.slug}`}
        className="self-start rounded-[var(--radius)] bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90"
      >
        {o.kind === 'course' ? 'View course' : o.kind === 'quiz' ? 'Details' : 'Details and seats'}
      </Link>
    </Card>
  );
}

function ShareProfile({ p }: { p: OrgProfile }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/o/${p.slug}`;
  const post = linkedInPostText({ kind: 'org', title: p.name, url });
  return (
    <Card title="Share">
      <div className="flex flex-col gap-2">
        <LinkedInShareButton post={post} url={url} />
        <NativeShareButton title={p.name} text={post} />
        <Button
          type="button"
          variant="secondary"
          onClick={async () => {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}
        >
          {copied ? (
            <Check className="size-4" aria-hidden />
          ) : (
            <Copy className="size-4" aria-hidden />
          )}
          {copied ? 'Copied' : 'Copy link'}
        </Button>
        <p className="text-xs text-muted-foreground">{LINKEDIN_TAG_TIP}</p>
      </div>
    </Card>
  );
}

/** Platform admins: the "Verified" badge, and hiding a profile from the public. */
function AdminControls({ p }: { p: OrgProfile }) {
  const me = useMe();
  const api = useApi();
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['orgProfilePage', p.slug] });
  const verify = useMutation({
    mutationFn: () => api.admin.setOrgVerified(p.id, !p.verified),
    onSuccess: refresh,
  });
  const hide = useMutation({
    mutationFn: () => api.admin.setOrgProfileHidden(p.id, !p.hidden),
    onSuccess: refresh,
  });
  if (!me.data?.isPlatformAdmin) return null;
  return (
    <Card title="Platform admin">
      <div className="flex flex-col gap-2">
        <Button
          type="button"
          variant="secondary"
          disabled={verify.isPending}
          onClick={() => verify.mutate()}
        >
          <ShieldCheck className="size-4" aria-hidden />
          {p.verified ? 'Remove Verified badge' : 'Mark as verified'}
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={hide.isPending}
          onClick={() => {
            if (p.hidden || window.confirm(`Hide ${p.name}'s profile from the public?`))
              hide.mutate();
          }}
        >
          <EyeOff className="size-4" aria-hidden />
          {p.hidden ? 'Show profile publicly again' : 'Hide from the public'}
        </Button>
        {p.hidden && (
          <p className="text-xs text-muted-foreground">
            Hidden: only members see it, and it&rsquo;s out of the directory.
          </p>
        )}
        <ErrorText error={verify.error ?? hide.error} />
      </div>
    </Card>
  );
}

/** `/o`: organizations with public profiles. */
export function OrgDirectoryPage() {
  const api = useApi();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get('q') ?? '');
  const type = (params.get('type') ?? '') as OrgType | '';
  const country = params.get('country') ?? '';
  const page = Number(params.get('page') ?? 0) || 0;
  const update = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) v ? next.set(k, v) : next.delete(k);
    if (!('page' in patch)) next.delete('page');
    setParams(next, { replace: true });
  };
  // Searches as you type, after a short pause.
  useEffect(() => {
    const t = setTimeout(() => {
      if ((params.get('q') ?? '') !== q.trim()) update({ q: q.trim() });
    }, 300);
    return () => clearTimeout(t);
  });
  const list = useQuery({
    queryKey: ['orgDirectory', { q: params.get('q'), type, country, page }],
    queryFn: () =>
      api.orgDirectory({
        q: params.get('q') || undefined,
        type: type || undefined,
        country: country || undefined,
        offset: page * ORG_DIRECTORY_PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });
  // Countries come with the first page; keep them while paging.
  const [countries, setCountries] = useState<string[]>([]);
  useEffect(() => {
    if (list.data?.countries) setCountries(list.data.countries);
  }, [list.data?.countries]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Organizations</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Universities, schools, companies and communities learning with Nirvik Ready, and what they
          offer.
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_12rem_12rem] sm:items-end">
        <SearchBox
          value={q}
          onChange={setQ}
          onSubmit={() => update({ q: q.trim() })}
          placeholder="Search by name, description or city"
          label="Search organizations"
        />
        <Filter
          id="dir-type"
          label="Type"
          value={type}
          onChange={(v) => update({ type: v })}
          options={[
            ['', 'All types'],
            ...ORG_TYPES.filter((t) => t !== 'nirvik').map(
              (t) => [t, ORG_TYPE_LABELS[t]] as [string, string],
            ),
          ]}
        />
        <Filter
          id="dir-country"
          label="Country"
          value={country}
          onChange={(v) => update({ country: v })}
          options={[
            ['', 'All countries'],
            ...countries
              .map((c) => [c, countryName(c)] as [string, string])
              .sort((a, b) => a[1].localeCompare(b[1])),
          ]}
        />
      </div>
      {list.isError && <ErrorText error={list.error} />}
      {list.data && !list.data.orgs.length && (
        <p className="text-sm text-muted-foreground">No organizations match.</p>
      )}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {list.data?.orgs.map((o) => (
          <Link
            key={o.id}
            to={`/o/${o.slug}`}
            className="flex min-w-0 gap-3 rounded-[var(--radius)] border border-border bg-card p-4 hover:bg-muted/50"
          >
            <OrgLogo name={o.name} url={o.logoUrl} size="lg" />
            <div className="min-w-0">
              <p className="flex min-w-0 items-center gap-1 font-medium">
                <span className="min-w-0 truncate">{o.name}</span>
                {o.verified && <VerifiedBadge />}
              </p>
              <p className="text-xs text-muted-foreground">
                {typeLabel(o.type)} · {place(o)}
              </p>
              {o.tagline && (
                <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{o.tagline}</p>
              )}
              {o.offerings > 0 && (
                <p className="mt-1 text-xs font-medium text-primary">
                  {o.offerings} open to everyone
                </p>
              )}
            </div>
          </Link>
        ))}
      </div>
      {list.data && (
        <Pager
          page={page}
          pageSize={ORG_DIRECTORY_PAGE_SIZE}
          total={list.data.total}
          busy={list.isFetching}
          onPage={(p) => update({ page: p ? String(p) : '' })}
        />
      )}
    </div>
  );
}
